import { useLocalStore } from 'mobx-react-lite';
import { MutableRefObject, useEffect, useRef } from 'react';

import { parseToHtml } from '@gem/modules/markdown';
import { getNodeByPath } from '@gem/utils';
import { getNodePath } from '@gem/utils';
import { encodeHtml } from '@gem/utils';

import Tasks from 'APP/Tasks';
import {
  ITextFormattingInputPresenter,
  TChangeHandler,
  TEditHandler,
  TSelectMentionHandler,
  TSubmitFilesHandler,
  TSubmitHandler,
} from 'APP/components/TextFormattingInput/TextFormattingInput.types';
import { useDraftInput } from 'APP/hooks/useDraftInput/useDraftInput';
import { useLinkPreview } from 'APP/hooks/useLinkPreview/useLinkPreview';
import { useUserTyping } from 'APP/hooks/useUserTyping/useUserTyping';
import { PayloadType } from 'APP/model/message/messageModel.types';
import bus from 'APP/packages/bus';
import { Features } from 'APP/packages/features/Features';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { Schedule } from 'STORE/Groups/Schedule';
import { InputPanel } from 'STORE/InputPanelsStore/InputPanel/InputPanel';

import { createTextMessage } from '../Tasks/createTextMessage';
import { editScheduledTextMessage } from '../Tasks/editScheduledTextMessage';
import { editScheduledVoiceMessage } from '../Tasks/editScheduledVoiceMessage';
import { editTextMessage } from '../Tasks/editTextMessage';
import { editVoiceMessage } from '../Tasks/editVoiceMessage';
import { scheduleTextMessage } from '../Tasks/scheduleTextMessage';
import { searchMentions } from './Tasks/searchMentions';

interface IInputSectionPresenter {
  inputPanel: InputPanel | null;
  group: Group | null;
  isScheduleGroup: boolean;
  isShowCommandsButton: boolean;
  isShowScheduleButton: boolean;
  value: string;
  scrollPosition: number;
  selectionPosition: Range | null;
  canSubmitWithEmptyValue: boolean;
  editMessage(): Promise<void>;
  createMessage(): Promise<void>;
  onUpdateDraftInput(): void;
  onChangeValue: TChangeHandler;
  onSubmitValue: TSubmitHandler;
  onSubmitFiles: TSubmitFilesHandler;
  onEditLastMessage: TEditHandler;
  onSelectMention: TSelectMentionHandler;
  onUpdateScrollPosition(value: number): void;
  onUpdateSelectionPosition(range: Range | null): void;
}

interface IUseInputSectionPresenter {
  presenter: IInputSectionPresenter;
  inputRef: MutableRefObject<ITextFormattingInputPresenter | null>;
}

export const useInputSectionPresenter = (): IUseInputSectionPresenter => {
  const linkPreview = useLinkPreview(Entities.GroupsStore.activeGroupId || '');
  const userTyping = useUserTyping(Entities.GroupsStore.activeGroupId || '');
  const draftInput = useDraftInput(Entities.GroupsStore.activeGroupId || '');
  const inputRef = useRef<ITextFormattingInputPresenter | null>(null);

  const presenter = useLocalStore<IInputSectionPresenter>(() => ({
    get inputPanel(): InputPanel | null {
      return Entities.InputPanelsStore.activeInputPanel;
    },

    get group(): Group | null {
      return Entities.GroupsStore.activeGroup;
    },

    get isScheduleGroup(): boolean {
      return presenter.group instanceof Schedule;
    },

    get isShowCommandsButton(): boolean {
      if (!presenter.inputPanel) {
        return false;
      }

      return presenter.inputPanel.bots.hasCommands;
    },

    get isShowScheduleButton(): boolean {
      if (!Features.isScheduledMessagesEnabled()) {
        return false;
      }

      if (presenter.group?.canBeScheduled) {
        const scheduledGroup = Entities.GroupsStore.getGroupById(presenter.group.scheduleGroupId);
        return Boolean(scheduledGroup?.messagesStore?.messages.length || -1 > 0);
      }

      return false;
    },

    get value(): string {
      return presenter.inputPanel?.value || '';
    },

    get scrollPosition(): number {
      return presenter.inputPanel?.scrollPosition || 0;
    },

    get selectionPosition(): Range | null {
      const inputElement = inputRef.current?.inputElement;

      if (!presenter.inputPanel?.selectionData || !inputElement) {
        return null;
      }

      const { anchorNodePath, focusNodePath, anchorOffset, focusOffset } =
        presenter.inputPanel.selectionData;
      const anchorNode = getNodeByPath(inputElement, anchorNodePath);
      const focusNode = getNodeByPath(inputElement, focusNodePath);

      if (!anchorNode || !focusNode) {
        return null;
      }

      const range = document.createRange();

      try {
        range.setStart(anchorNode, anchorOffset);
        range.setEnd(focusNode, focusOffset);
      } catch {
        return null;
      }

      return range;
    },

    get canSubmitWithEmptyValue(): boolean {
      if (!presenter.inputPanel?.editMessage) {
        return false;
      }

      const payloadType = presenter.inputPanel.editMessage.payload.payloadType;

      const allowedEmptyValueTypes = [PayloadType.VoiceMessage];

      return allowedEmptyValueTypes.includes(payloadType);
    },

    async editMessage(): Promise<void> {
      if (!presenter.group || !presenter.inputPanel?.editMessage) {
        return;
      }

      if (presenter.isScheduleGroup) {
        if (presenter.inputPanel.editMessage.payload.payloadType === PayloadType.VoiceMessage) {
          await editScheduledVoiceMessage(presenter.group.id, presenter.inputPanel.editMessage);
        } else {
          await editScheduledTextMessage(presenter.group.id, presenter.inputPanel.editMessage);
        }

        return;
      }

      if (presenter.inputPanel.editMessage.payload.payloadType === PayloadType.VoiceMessage) {
        await editVoiceMessage(presenter.group.id, presenter.inputPanel.editMessage);
      } else {
        await editTextMessage(presenter.group.id, presenter.inputPanel.editMessage);
      }
    },

    async createMessage(): Promise<void> {
      if (!presenter.group) {
        return;
      }

      if (presenter.isScheduleGroup) {
        await scheduleTextMessage(presenter.group.id);
      } else {
        await createTextMessage(presenter.group.id);
      }
    },

    onChangeValue(value: string): void {
      presenter.inputPanel!.setValue(value);

      if (!presenter.isScheduleGroup && !presenter.inputPanel!.isEditMode) {
        userTyping.send();
      }
    },

    async onSubmitValue(): Promise<void> {
      const isEmptyValue = !presenter.value.trim();

      linkPreview.reset();
      linkPreview.searchLinks(presenter.value);

      if (isEmptyValue && !presenter.canSubmitWithEmptyValue) {
        return;
      }

      if (presenter.inputPanel?.isEditMode) {
        await presenter.editMessage();
      } else {
        await presenter.createMessage();
      }
    },

    onSubmitFiles(files: File[]): void {
      Tasks.messaging.sendFiles({
        groupId: presenter.group!.id,
        files,
      });
    },

    onEditLastMessage(): void {
      if (!presenter.group) {
        return;
      }

      if (!presenter.inputPanel?.isEmptyValue || presenter.inputPanel?.isEditMode) {
        return;
      }

      const message = Tasks.messaging.getLastEditableMessage(presenter.group.id);
      if (!message) {
        return;
      }

      Tasks.messaging.startEditMessage({
        groupId: presenter.group!.id,
        editMessageId: message.id,
      });
    },

    onSelectMention(mention: string | null): void {
      if (!mention) {
        presenter.inputPanel!.mention.reset();
        return;
      }

      searchMentions(presenter.inputPanel!, mention.substring(1));
    },

    onUpdateDraftInput(): void {
      if (
        !presenter.group?.draftInput?.text ||
        !presenter.inputPanel ||
        presenter.inputPanel.isEditMode
      ) {
        return;
      }

      const text = encodeHtml(presenter.group.draftInput.text);
      const html = parseToHtml(text);

      presenter.inputPanel.setValue(html);
    },

    onUpdateScrollPosition(value: number): void {
      if (!presenter.inputPanel) {
        return;
      }

      presenter.inputPanel.setScrollPosition(value);
    },

    onUpdateSelectionPosition(range: Range | null): void {
      const inputElement = inputRef.current?.inputElement;

      if (!presenter.inputPanel || !range || !inputElement) {
        return;
      }

      presenter.inputPanel.setSelectionData({
        anchorNodePath: getNodePath(inputElement, range.startContainer),
        focusNodePath: getNodePath(inputElement, range.endContainer),
        anchorOffset: range.startOffset,
        focusOffset: range.endOffset,
      });
    },
  }));

  useEffect(() => {
    const reactions = [
      bus.messaging.subscribeSelectEmoji(inputRef.current!.onPutText),
      bus.messaging.subscribeStartReplyMessage(() => {
        if (presenter.inputPanel?.quote) {
          Tasks.messaging.endQuote(null);
        }
        inputRef.current!.setFocus();
      }),
      bus.messaging.subscribeStartQuote(() => {
        if (presenter.inputPanel?.replyMessage) {
          Tasks.messaging.endReplyMessage(null);
        }
        inputRef.current!.setFocus();
      }),
      bus.messaging.subscribeSelectMention(inputRef.current!.onPutMention),
    ];

    return () => {
      reactions.forEach((dispose) => dispose());
    };
  }, []);

  useEffect(() => {
    //this value changed after send message with button
    draftInput.update(presenter.inputPanel?.value || '');
    linkPreview.update(presenter.inputPanel?.value || '');
  }, [presenter.inputPanel?.value]);

  useEffect(() => {
    inputRef.current!.setFocus();
    presenter.onUpdateDraftInput();
    inputRef.current!.setScrollPosition(presenter.scrollPosition);
    presenter.selectionPosition && inputRef.current!.setSelection(presenter.selectionPosition);
  }, [presenter.group]);

  useEffect(() => {
    if (Entities.App.uiLayer === 0) {
      inputRef.current!.setFocus();
    } else {
      inputRef.current!.unsetFocus();
    }
  }, [Entities.App.uiLayer]);

  useEffect(() => {
    if (Features.isScheduledMessagesEnabled() && presenter.group?.canBeScheduled) {
      Tasks.group.loadScheduledMessages(presenter.group.id);
    }
  }, [presenter.group?.id]);

  useEffect(() => {
    let unsubscribeDeleteMessage: () => void;
    const { replyMessage, quote } = presenter.inputPanel!;
    const { id: groupId } = presenter.group!;

    if ((replyMessage?.id || quote) && groupId) {
      unsubscribeDeleteMessage = bus.messaging.subscribeDeleteMessage(
        groupId,
        replyMessage?.id,
        () => {
          replyMessage && Tasks.messaging.endReplyMessage(groupId);
          quote && Tasks.messaging.endQuote(groupId);
          unsubscribeDeleteMessage();
        }
      );
    }

    return () => {
      unsubscribeDeleteMessage?.();
    };
  }, [presenter.inputPanel?.replyMessage, presenter.group?.id]);

  return { presenter, inputRef };
};
